/**
 * Theme: light / dark / system. One module for the pre-React init
 * (`applyThemeFromStorage`, called from main.tsx before mount so dark
 * users get no flash of light UI), the live prefers-color-scheme watch
 * while "system" is selected, and the `useTheme()` hook for the
 * picker. One module so the picker reuses the boot rule instead of
 * re-deriving it (and no circular import).
 * CLAUDE.md > Front-end design tokens; docs/customer-app.md > Front-end > Theme
 */
import { useEffect, useState } from "react";

const STORAGE_KEY = "customer-app:theme";

export type ThemePref = "light" | "dark" | "system";

function isThemePref(v: unknown): v is ThemePref {
  return v === "light" || v === "dark" || v === "system";
}

export function readPref(): ThemePref {
  if (typeof localStorage === "undefined") return "system";
  const v = localStorage.getItem(STORAGE_KEY);
  return isThemePref(v) ? v : "system";
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/** Apply the dark class based on stored pref + system. Safe to call
 * before React has mounted - only touches <html>.classList and the
 * <meta theme-color>. */
export function applyThemeFromStorage(): void {
  if (typeof document === "undefined") return;
  const pref = readPref();
  const dark = pref === "dark" || (pref === "system" && systemPrefersDark());
  document.documentElement.classList.toggle("dark", dark);

  // Sync the address-bar / status-bar tint on PWA installs + mobile
  // browsers. The hex literals here match --color-surface in
  // index.css - duplicated on purpose: meta theme-color doesn't
  // accept var() values, and reading the computed property of
  // <html> at boot would force a paint and a flash.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = dark ? "#070b13" : "#f3f4f1";
  syncNativeStatusBar(dark);
}

/** Native app (Capacitor): kleur de systeem-statusbalk mee met het
 * thema en zet de icoonstijl leesbaar. No-op op het web en wanneer de
 * plugin ontbreekt - de app mag hier nooit op stuklopen. */
function syncNativeStatusBar(dark: boolean): void {
  const cap = (window as unknown as {
    Capacitor?: {
      isNativePlatform?: () => boolean;
      Plugins?: {
        StatusBar?: {
          setBackgroundColor?: (o: { color: string }) => Promise<void>;
          setStyle?: (o: { style: string }) => Promise<void>;
        };
      };
    };
  }).Capacitor;
  if (!cap?.isNativePlatform?.()) return;
  const sb = cap.Plugins?.StatusBar;
  if (!sb) return;
  // Style DARK = lichte iconen (op een donkere balk) en omgekeerd.
  void sb.setBackgroundColor?.({ color: dark ? "#070b13" : "#f3f4f1" });
  void sb.setStyle?.({ style: dark ? "DARK" : "LIGHT" });
}

export function useTheme(): {
  pref: ThemePref;
  setPref: (p: ThemePref) => void;
} {
  const [pref, setPrefState] = useState<ThemePref>(readPref);

  // While the user has "system" selected, follow the OS toggle live.
  useEffect(() => {
    if (pref !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyThemeFromStorage();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [pref]);

  function setPref(p: ThemePref) {
    localStorage.setItem(STORAGE_KEY, p);
    setPrefState(p);
    applyThemeFromStorage();
  }

  return { pref, setPref };
}
